import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import gsap from "gsap";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { constants } from "utils";
import { trackClickedButton } from "utils/analytics";
import { SelectDropdown } from "components/shared/FilterDropdowns/FilterDropdowns";
import { classDisplayName } from "components/shared/ClassroomFilter/groupClassesBySchoolYear";
import { EditClassButton } from "./EditClassButton";
import { LessonGroupsView } from "./LessonGroupsView";
import { StandardGroupsView } from "./StandardGroupsView";
import { QuickGroupsSection } from "./QuickGroupsSection";
import { CreateGroupsBar } from "./CreateGroupsBar";
import { createControlsFor, warmUpPickerUrl } from "./warmUpEntry";
import { ListingHeader } from "./ListingTitle";
import { GroupsSkeleton } from "./GroupsSkeleton";
import { focusPickerParams, sortBundles, SORT_OPTIONS } from "./assignmentDraft";
import { useLastFocus } from "hooks/useLastFocus/useLastFocus";
import { useFeatureFlagStatus } from "hooks/useFeatureFlag";
import { manageGroupsLayoutFor } from "./manageGroupsLayout";
import { OneListPage } from "./OneListPage";
import { DEFAULT_LIST_SETTINGS } from "./oneListCards";

const LessonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

// Renders a classroom's lesson-picker assignments: the Class selector + the
// reusable Create Groups bar above the Quick Groups list and one
// LessonGroupsView per assigned lesson.
export const LessonGroupsList = ({
  lessons,
  quickGroups = [],
  onQuickGroupsChanged,
  warmUpAssignments = [],
  warmUpError = null,
  refetchWarmUps,
  replaceWarmUpAssignment,
  onLastWarmUpArchived,
  focusCreateGroups = false,
  // The one list asks for focus on Create Groups whatever else the class has.
  focusCreateGroupsInList = false,
  onCreateGroupsFocused,
  unassignedCodes = [],
  classroomId,
  curriculumId,
  totalCodes,
  classes,
  selectedClassId,
  onClassChange,
  onEditClass,
  onRefresh,
  onLessonCopied,
  onAccountRefresh,
  sourcesSettled = true,
  listSettings = DEFAULT_LIST_SETTINGS,
  onListSettingsChange,
  sourcesReadAt,
}) => {
  const history = useHistory();
  const warmUpChallenges = useFeatureFlagStatus("warmUpChallenges");
  const layout = manageGroupsLayoutFor(warmUpChallenges);
  const [sortBy, setSortBy] = useState("recent");
  // Focused edit mode (Figma 4884-52207): while one lesson is being edited the page
  // drops to that accordion alone — no class picker, Create Groups bar, quick groups,
  // listing header or sibling lessons.
  const [focusedId, setFocusedId] = useState(null);
  const stackRef = useRef(null);

  // Only the enter/leave transition animates — not the first paint of the page.
  const didMountRef = useRef(false);
  useLayoutEffect(() => {
    const el = stackRef.current;
    if (!didMountRef.current) { didMountRef.current = true; return undefined; }
    if (!el) return undefined;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduced) return undefined;
    // Scoped so a fast enter/exit can't leave tweens running on unmounted nodes.
    const ctx = gsap.context(() => {
      gsap.from(el, { autoAlpha: 0, y: 12, duration: 0.35, ease: "power2.out" });
    }, stackRef);
    return () => ctx.revert();
  }, [focusedId]);
  const sortedLessons = useMemo(() => sortBundles(lessons, sortBy), [lessons, sortBy]);
  // Every entry the class shows, quick groups included — any of them can be the
  // last thing worked on. Unsorted, so the Sort By choice never moves the default.
  const entries = useMemo(() => [...lessons, ...quickGroups], [lessons, quickGroups]);
  const lastFocus = useLastFocus(classroomId, entries);

  const handleAssignNewLesson = () => {
    trackClickedButton("Assign New Lesson", {
      page_name: "Dashboard",
      source: "lesson_groups_list",
      class: classroomId || null,
    });
    const params = new URLSearchParams({
      classroomId: classroomId || "",
      totalAvailable: String(unassignedCodes.length),
      // Default the picker to the class's most-recently-touched lesson/standard.
      ...focusPickerParams(lastFocus),
    });
    history.push(`${constants.LESSONS_STANDARDS_PATH}?${params}`);
  };

  const handleCreateQuickGroup = () => {
    trackClickedButton("Quick Group", {
      page_name: "Dashboard",
      source: "lesson_groups_list",
      class: classroomId || null,
    });
    const params = new URLSearchParams({
      classroomId: classroomId || "",
      classId: classroomId || "",
      // Open the grade dropdown on the class's working grade, not the teacher's.
      ...(lastFocus?.grade ? { grade: lastFocus.grade } : {}),
    });
    history.push(`${constants.CREATE_QUICK_GROUP_PATH}?${params}`);
  };

  const handleOpenCreateMenu = () => {
    trackClickedButton("Create Groups", {
      page_name: "Dashboard",
      source: "lesson_groups_list",
      class: classroomId || null,
    });
  };

  const handleCreateWarmUpGroups = () => {
    trackClickedButton("Warm-Up Groups", {
      page_name: "Dashboard",
      source: "lesson_groups_list",
      class: classroomId || null,
    });
    history.push(warmUpPickerUrl(classroomId));
  };

  const renderLesson = (entry, listCard) => (
    entry.bundle_type === "standard" ? (
      <StandardGroupsView
        key={entry.assignment_id}
        standard={entry.standard}
        assignments={entry.assignments}
        assignmentId={entry.assignment_id}
        lastUpdatedAt={entry.last_updated_at ?? null}
        classroomId={classroomId}
        selectedClassId={classroomId}
        poolCodes={unassignedCodes}
        poolTotal={totalCodes}
        onRefresh={onRefresh}
        classes={classes}
        onLessonCopied={onLessonCopied}
        onAccountRefresh={onAccountRefresh}
        onEditingChange={(editing) => setFocusedId(editing ? entry.assignment_id : null)}
        listCard={listCard}
      />
    ) : (
      <LessonGroupsView
        key={entry.assignment_id || entry.lesson?.id}
        embedded
        lesson={entry.lesson}
        assignments={entry.assignments}
        assignmentId={entry.assignment_id}
        lastUpdatedAt={entry.last_updated_at ?? null}
        classroomId={classroomId}
        curriculumId={curriculumId}
        selectedClassId={classroomId}
        poolCodes={unassignedCodes}
        poolTotal={totalCodes}
        onRefresh={onRefresh}
        classes={classes}
        onLessonCopied={onLessonCopied}
        onAccountRefresh={onAccountRefresh}
        onEditingChange={(editing) => setFocusedId(editing ? entry.assignment_id : null)}
        listCard={listCard}
      />
    )
  );

  const isFocused = focusedId !== null;
  const visibleLessons = isFocused
    ? sortedLessons.filter((entry) => entry.assignment_id === focusedId)
    : sortedLessons;

  return (
    <div>
      {layout === "one_list" && (
        <OneListPage
          focusedKey={isFocused ? `lesson:${focusedId}` : null}
          classes={classes}
          selectedClassId={selectedClassId}
          onClassChange={onClassChange}
          onEditClass={onEditClass}
          lessons={lessons}
          quickGroups={quickGroups}
          warmUps={warmUpAssignments}
          classroomId={classroomId}
          renderLesson={renderLesson}
          settled={sourcesSettled}
          available={unassignedCodes.length}
          total={totalCodes}
          createMenu={{
            onOpen: handleOpenCreateMenu,
            onCreateLessonGroups: handleAssignNewLesson,
            onCreateQuickGroup: handleCreateQuickGroup,
            onCreateWarmUpGroups: handleCreateWarmUpGroups,
            focusRequest: focusCreateGroupsInList,
            onFocusRequestDone: onCreateGroupsFocused,
          }}
          warmUpError={warmUpError}
          listSettings={listSettings}
          onListSettingsChange={onListSettingsChange}
          sourcesReadAt={sourcesReadAt}
          source="lesson_groups_list"
          warmUpActions={{
            source: "lesson_groups_list",
            classes,
            onCopied: onLessonCopied,
            onAccountRefresh,
            refetchWarmUps,
            replaceAssignment: replaceWarmUpAssignment,
            onArchived: onRefresh,
          }}
          onNoCardLeft={onLastWarmUpArchived}
          onQuickGroupsChanged={() => Promise.all([onQuickGroupsChanged?.(), onRefresh?.()])}
        />
      )}
      {!isFocused && layout !== "one_list" && (
      <>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8, marginBottom: 16 }}>
        <div style={{ maxWidth: 200, width: "100%" }}>
          <SelectDropdown
            label="Class"
            value={selectedClassId}
            displayText={classDisplayName(classes?.find((c) => c.id === selectedClassId)) || "Select a class"}
            options={(classes || []).map((cls) => ({ value: cls.id, label: cls.name, school_year: cls.school_year }))}
            groupBySchoolYear
            onChange={onClassChange}
          />
        </div>
        {onEditClass && <EditClassButton onClick={onEditClass} />}
      </div>

      {layout === "pending" ? <GroupsSkeleton withHeader={false} /> : (
      <>
      {/* Availability + Create Groups (Figma 4277-12534) */}
      <CreateGroupsBar
        available={unassignedCodes.length}
        total={totalCodes}
        onCreateLessonGroups={handleAssignNewLesson}
        onCreateQuickGroup={handleCreateQuickGroup}
        createControls={createControlsFor(warmUpChallenges)}
        onOpenCreateMenu={handleOpenCreateMenu}
        onCreateWarmUpGroups={handleCreateWarmUpGroups}
        menuResetKey={classroomId}
        focusMenuButton={focusCreateGroups}
        onMenuButtonFocused={onCreateGroupsFocused}
      />

      {/* Quick groups list first, then the assigned lessons (same top bar for both). */}
      <QuickGroupsSection
        quickGroups={quickGroups}
        classroomId={classroomId}
        onChanged={onQuickGroupsChanged}
        style={{ marginBottom: 24 }}
      />
      </>
      )}
      </>
      )}

      <LessonStack ref={stackRef}>
        {!isFocused && layout === "sections" && lessons.length > 0 && (
          <ListingHeader
            title="Lessons"
            sortValue={sortBy}
            sortOptions={SORT_OPTIONS}
            onSortChange={setSortBy}
          />
        )}
        {layout === "sections" && visibleLessons.map((entry) => renderLesson(entry))}
      </LessonStack>
    </div>
  );
};
